import { Box, Button, Typography } from "@mui/material";
import { FieldArray, Form, Formik } from "formik";
import React, { useMemo, useState } from "react";
import { AlertComponent, MUITable } from "../../../../../common/components";
import {
  getUserErrorMessage,
  headers,
  User,
  validationSchema,
} from "../../../add-tenant/add-new-tenet-helper";
import "../../../add-tenant/add-new-tenant.scss";
import "./tenant-user-details.scss";
import icons from "../../../../../assets/icons/icons";
import isEqual from "lodash/isEqual";
import {
  resendCredentialEmail,
  updateTenetUsers,
} from "../../../../../api/tenets";
import { useSetLoading } from "../../../../../common/hooks/appearance";
import { AlertMessage } from "../../tenant-details-types";
import { useResourcePermissions } from "../../../../../common/hooks/resource-permissions";
import MODULES from "../../../../../constants/modules";
import { useLogin } from "../../../../../common/hooks/useUser";
import CustomTooltip from "../../../../../common/components/custom-tooltip";

interface TenantUserDetailsProps {
  tenantData: any;
  tenetRoles: any;
  setTenantData?: any;
  partnerDetails: any;
}

const TenantUserDetails: React.FC<TenantUserDetailsProps> = ({
  tenantData,
  tenetRoles,
  setTenantData,
  partnerDetails,
}) => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [alertMessage, setAlertMessage] = useState<AlertMessage>({
    open: false,
  });
  const { canWrite, canDelete } = useResourcePermissions(MODULES.PARTNER_LIST);

  const userDetails = useLogin();
  const userRole = userDetails.userDetails.roles.role;

  const handleUsersMapToInitialUsers = (users: any = []) => {
    return (
      users?.map((user: any) => ({
        id: user.id,
        firstName: user.first_name || "",
        lastName: user.last_name || "",
        email: user.email || "",
        phoneNumber: user.phone_number || "",
        roleId: user.role?.id || "",
        isPrimaryUser: user.is_primary_user || false,
        shouldResendUserCredentials: user.shouldResendUserCredentials || false,
      })) || []
    );
  };

  const initialUsers = handleUsersMapToInitialUsers(tenantData?.users);

  const formikValidationSchema = validationSchema[2];

  const onHandleChange = (
    rowIndex: number,
    key: string,
    value: any,
    setFieldValue?: any,
    values?: any
  ) => {
    const userValues: Partial<User>[] = [...values.users];

    userValues[rowIndex] = {
      ...userValues[rowIndex],
      [key]: value,
    };

    setFieldValue("users", userValues);
  };

  const handleAddUser = (push: any, errors: any, values: any) => {
    const errMessage = getUserErrorMessage(errors.users, values.users, 5);
    if (errMessage) {
      setAlertMessage({
        message: (
          <Typography sx={{ fontSize: "13px" }}>
            {typeof errMessage === "string"
              ? "You're doing great! Just clear up the issues below to add the new user.."
              : "Please fill in all the fields before adding the new user."}
            <br />
            {errMessage || ""}
          </Typography>
        ),
        open: true,
      });

      return;
    }
    setAlertMessage({
      message:
        "First Name and Last Name cannot be changed once saved, Please add proper names.",
      type: "warning",
      open: true,
    });
    push({
      firstName: "",
      lastName: "",
      email: "",
      phoneNumber: "",
      roleId: "",
      isPrimaryUser: false,
    });
  };

  // API to update the tenant user details here
  const updateTenantUserDetails = async (data: any, resetForm: any) => {
    try {
      setIsLoading(true);
      const resp = await updateTenetUsers(partnerDetails?.id, data);
      setAlertMessage({
        open: true,
        message: "Partner user details updated successfully.",
        type: "success",
      });

      const updatesUsersData = resp?.data?.data || [];
      resetForm({
        values: {
          users: handleUsersMapToInitialUsers(updatesUsersData),
        },
      });
      setIsLoading(false);
      setTenantData((prev: any) => ({ ...prev, users: updatesUsersData }));
    } catch (err: any) {
      console.error("Error updating tenant user details:", err);
      const errMessage =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message ||
        "Something went wrong!";
      setAlertMessage({
        open: true,
        message: errMessage,
        type: "error",
      });
      setIsLoading(false);
    }
  };

  const handleValidateUsers = (values: any, errors: any, handleSubmit: any) => {
    const errMessage: any = getUserErrorMessage(
      errors.users,
      values.users,
      5,
      true
    );
    if (errMessage) {
      setAlertMessage({
        open: true,
        message: (
          <Typography sx={{ fontSize: "13px" }}>
            {!errMessage.includes("almost there!") ? (
              <>
                You&apos;re almost there! Please review the errors below. <br />
              </>
            ) : (
              ""
            )}
            {errMessage}
          </Typography>
        ),
        type: "error",
      });
      return;
    }
    handleSubmit();
  };

  const handlePayload = (values: any) => {
    const { users } = values;

    const newUsersData = [];
    const existingUsersIds: any[] = [];
    const existingUsers: any[] = [];
    for (var item of users) {
      const cleanedItem = { ...item };
      delete cleanedItem.shouldResendUserCredentials;
      if (!cleanedItem?.id) {
        newUsersData.push({ ...cleanedItem, isCreate: true });
      }
      if (cleanedItem?.id) {
        existingUsersIds.push(cleanedItem.id);
        existingUsers.push(cleanedItem);
      }
    }

    const editedExistingData = initialUsers.map((item: any, index: number) => {
      if (!existingUsersIds.includes(item.id)) {
        return { isDelete: true, id: item.id };
      }
      const isUserValueChanged = !isEqual(item, existingUsers[index]);

      if (isUserValueChanged) {
        const oldItem = existingUsers[index];
        return { isUpdate: true, ...oldItem };
      }
      return false;
    });

    const filterEditedExistingData = editedExistingData.filter(
      (item: any) => !!item?.id
    );

    const payload = { users: [...newUsersData, ...filterEditedExistingData] };
    return payload;
  };

  const handleResendCredentialEmail = async (user: any) => {
    setIsLoading(true);
    try {
      await resendCredentialEmail(partnerDetails?.id, user?.id);
      setAlertMessage({
        open: true,
        message: "User credential email resent successfully.",
        type: "success",
      });
    } catch (error: any) {
      console.error("Error resending credential email:", error);
      setAlertMessage({
        open: true,
        message: "Error resending user credential email.",
        type: "error",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const showLoading = useMemo(() => isLoading, [isLoading]);
  useSetLoading(showLoading);

  return (
    <Box className="tenant-details-user-wrapper">
      <Formik
        initialValues={{ users: initialUsers }}
        enableReinitialize
        validationSchema={formikValidationSchema}
        validateOnBlur
        onSubmit={(values, { resetForm }) => {
          const payload = handlePayload(values);
          updateTenantUserDetails(payload, resetForm);
        }}
      >
        {({ values, errors, setFieldValue, handleSubmit, resetForm }) => {
          return (
            <Form>
              <FieldArray name="users">
                {({ push, remove }) => (
                  <>
                    <div className="user-table-wrapper">
                      <MUITable
                        headers={headers}
                        data={
                          values?.users?.map((user: any, index: number) => ({
                            ...user,
                            delete: (
                              <>
                                {["Super Admin", "Admin"].includes(userRole) ? (
                                  <>
                                    <CustomTooltip title="Archive User" placement="bottom" arrow={false}>
                                      <img
                                        src={icons.DeleteIcon}
                                        alt="delete"
                                        className={`delete-icon ${user?.isPrimaryUser || canDelete ? "delete-icon-disable" : ""}`}
                                        onClick={() => {
                                          if (!user?.isPrimaryUser && canDelete)
                                            remove(index);
                                        }}
                                      />
                                    </CustomTooltip>
                                    <span
                                      style={{
                                        marginLeft: "8px",
                                        width: "24px",
                                      }}
                                    >
                                      {user.shouldResendUserCredentials ? (
                                        <CustomTooltip title="Resend User Credentials" placement="bottom" arrow={false}>
                                          <img
                                            src={icons.EmailResendIcons}
                                            alt="resend email"
                                            width={20}
                                            className="delete-icon"
                                            onClick={() =>
                                              handleResendCredentialEmail(user)
                                            }
                                          />
                                        </CustomTooltip>
                                      ) : (
                                        <span
                                          style={{
                                            display: "inline-block",
                                            width: 20,
                                            height: 20,
                                          }}
                                        />
                                      )}
                                    </span>
                                  </>
                                ) : (
                                  <>
                                    <img
                                      src={icons.DeleteIcon}
                                      alt="delete"
                                      className={`delete-icon ${user?.isPrimaryUser || canDelete ? "delete-icon-disable" : ""}`}
                                      onClick={() => {
                                        if (!user?.isPrimaryUser && canDelete)
                                          remove(index);
                                      }}
                                    />
                                  </>
                                )}
                              </>
                            ),
                          })) || []
                        }
                        isShowUserCredentialEmailOption={[
                          "Super Admin",
                          "Admin",
                        ].includes(userRole)}
                        isStriped={false}
                        selectDropDownOptions={{
                          roleId: tenetRoles || [],
                        }}
                        onHandleChange={(
                          rowIndex: number,
                          key: string,
                          value: any
                        ) => {
                          onHandleChange(
                            rowIndex,
                            key,
                            value,
                            setFieldValue,
                            values
                          );
                        }}
                        onSelectChange={(rowIndex, header, value) => {
                          onHandleChange(
                            rowIndex,
                            header,
                            value,
                            setFieldValue,
                            values
                          );
                        }}
                      />
                    </div>

                    <Box className="add-user-button-wrapper">
                      <button
                        type="button"
                        onClick={() => handleAddUser(push, errors, values)}
                        disabled={
                          tenantData?.basic_details?.tenant_type !==
                            "SuperTenant" &&
                          values?.users?.length >=
                            tenantData?.purchased_subscription_plan
                              ?.no_of_users &&
                          tenantData?.purchased_subscription_plan
                            ?.no_of_users !== -1
                        }
                      >
                        Add User
                      </button>
                    </Box>
                    <Box className="save-user-button-wrapper">
                      <Box sx={{ display: "flex", marginRight: "-8px" }}>
                        {!isEqual(initialUsers, values.users) && (
                          <button
                            type="button"
                            className="cancel-button"
                            onClick={() => {
                              resetForm({ values: { users: initialUsers } });
                            }}
                          >
                            Cancel
                          </button>
                        )}
                        {canWrite && (
                          <button
                            type="button"
                            className="cancel-button next-button-wrapper"
                            disabled={isEqual(initialUsers, values.users)}
                            onClick={() => {
                              handleValidateUsers(values, errors, handleSubmit);
                            }}
                          >
                            Save
                          </button>
                        )}
                      </Box>
                    </Box>
                  </>
                )}
              </FieldArray>
            </Form>
          );
        }}
      </Formik>
      {alertMessage?.open && (
        <AlertComponent
          message={alertMessage?.message}
          type={alertMessage?.type || "error"}
          open={alertMessage?.open}
          onClose={() => {
            setAlertMessage({ open: false });
          }}
        />
      )}
    </Box>
  );
};

export default TenantUserDetails;
